import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  Divider,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DownloadIcon from "@mui/icons-material/Download";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import CustomButton from "../../components/CustomButton/Button";
import { GET_SENT_EMAILS } from "../../utils/url";
import { getFormattedDate } from "../../utils/commonFunctions";
import getPreSignedUrl from "../../utils/generatePreSignedUrl";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

interface SentEmail {
  transactionId: string;
  status: string;
  emailType: string;
  senderEmail: string;
  toEmail: string;
  subject: string;
  emailContentUrl: string;
  opens: number | null;
  clicks: number | null;
  dateSent: string;
  runId: string;
  campaignId: string;
  patronId: string | null;
  merchantId: string | null;
  subCampaignId: string;
  firstOpenTime: string | null;
}

interface BusinessEmailPageProps {
  businessId: string;
}

const BusinessEmailPage: React.FC<BusinessEmailPageProps> = ({
  businessId
}) => {
  const [emails, setEmails] = useState<SentEmail[]>([]);
  const [apiStatus, setApiStatus] = useState<"idle" | "fetching" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState<SentEmail | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const fetchEmails = async (page: number, size: number) => {
    if (!businessId) return;
    setApiStatus("fetching");
    setError("");
    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios({
        method: "get",
        url: `${apiEndpoint}${GET_SENT_EMAILS(businessId)}?PageNumber=${
          page + 1
        }&PageSize=${size}`,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      });
      const data = response.data;
      setEmails(data?.Data || []);
      setTotalCount(data?.TotalCount || 0);
      setApiStatus("idle");
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch sent emails.");
      setApiStatus("error");
    }
  };

  useEffect(() => {
    fetchEmails(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, currentPage, pageSize]);

  const handleChangePage = (_: any, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleDownload = async (email: SentEmail) => {
    setDownloadingIds((prev) => new Set(prev).add(email.transactionId));
    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const bucketName = process.env.REACT_APP_SENT_EMAIL_BUCKET_NAME;
      const key = email.emailContentUrl.split("/").slice(1).join("/");

      const presignResponse = await getPreSignedUrl(
        bucketName || "",
        key,
        accessToken,
        "GET"
      );

      if (presignResponse?.PreSignUrl) {
        const downloadResponse = await fetch(presignResponse.PreSignUrl);
        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${email.transactionId}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Failed to download email:", error);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(email.transactionId);
        return next;
      });
    }
  };

  const columns: any[] = [
    {
      id: "subject",
      label: "Subject",
      format: (value: any) => value || "-"
    },
    {
      id: "toEmail",
      label: "To Email",
      format: (value: any) => value || "-"
    },
    {
      id: "senderEmail",
      label: "Sender Email",
      format: (value: any) => value || "-"
    },
    {
      id: "emailType",
      label: "Email Type",
      format: (value: any) =>
        value ? <Chip label={value} size="small" variant="outlined" /> : "-"
    },
    {
      id: "status",
      label: "Status",
      format: (value: any) =>
        value ? (
          <Chip
            label={value}
            size="small"
            color={value === "email_sent" ? "success" : "default"}
          />
        ) : (
          "-"
        )
    },
    {
      id: "dateSent",
      label: "Date Sent",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "actions",
      label: "Actions",
      format: (_: any, row: SentEmail) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEmail(row);
              }}
            >
              <RemoveRedEyeIcon sx={{ color: "#192f67" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Email">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={downloadingIds.has(row.transactionId)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(row);
                }}
              >
                {downloadingIds.has(row.transactionId) ? (
                  <CircularProgress size={18} sx={{ color: "#192f67" }} />
                ) : (
                  <DownloadIcon sx={{ color: "#192f67" }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              margin: "0",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "1.25",
              fontFamily: "Montserrat, sans-serif",
              fontStyle: "normal",
              color: "#192F67",
              marginBottom: "0.35em"
            }}
          >
            Sent Emails
          </Typography>
          {/* <Typography variant="body2" color="text.secondary">
            Business ID: <strong>{businessId}</strong>
          </Typography> */}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <CustomTableWithPagination
        columns={columns}
        data={emails}
        apiStatus={apiStatus}
        payloadSize={pageSize}
        currentPage={currentPage}
        totalCount={totalCount}
        isShowPagination={true}
        noDataFoundMessage="No emails found for this business."
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />

      {/* Email Details Modal */}
      <Dialog
        open={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Email Details</Typography>
            <IconButton onClick={() => setSelectedEmail(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedEmail && (
            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
              <DetailRow
                label="Transaction ID"
                value={selectedEmail.transactionId}
              />
              <DetailRow label="Subject" value={selectedEmail.subject} />
              <DetailRow label="To Email" value={selectedEmail.toEmail} />
              <DetailRow
                label="Sender Email"
                value={selectedEmail.senderEmail}
              />
              <DetailRow label="Email Type" value={selectedEmail.emailType} />
              <DetailRow label="Status" value={selectedEmail.status} />
              <DetailRow
                label="Date Sent"
                value={getFormattedDate(selectedEmail.dateSent)}
              />
              <DetailRow label="Run ID" value={selectedEmail.runId} />
              <DetailRow
                label="Campaign ID"
                value={selectedEmail.campaignId || "-"}
              />
              <DetailRow
                label="Sub-Campaign ID"
                value={selectedEmail.subCampaignId || "-"}
              />
              <DetailRow
                label="Patron ID"
                value={selectedEmail.patronId || "-"}
              />
              <DetailRow
                label="Merchant ID"
                value={selectedEmail.merchantId || "-"}
              />
              <DetailRow
                label="Opens"
                value={selectedEmail.opens?.toString() ?? "-"}
              />
              <DetailRow
                label="Clicks"
                value={selectedEmail.clicks?.toString() ?? "-"}
              />
              <DetailRow
                label="First Open Time"
                value={
                  selectedEmail.firstOpenTime
                    ? getFormattedDate(selectedEmail.firstOpenTime)
                    : "-"
                }
              />
              <DetailRow
                label="Email Content URL"
                value={selectedEmail.emailContentUrl}
              />
            </Box>
          )}
        </DialogContent>
        <Divider />
      </Dialog>
    </Box>
  );
};

const DetailRow = ({
  label,
  value
}: {
  label: string;
  value: string | undefined;
}) => (
  <Box display="flex" gap={1}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 160, fontWeight: 500 }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
      {value || "-"}
    </Typography>
  </Box>
);

export default BusinessEmailPage;
